import angular from "angular";

interface IAuthService {
  append(config, deferred): void;

  loginConfirmed(): void;
}

namespace app {

  export class AuthService implements angular.IServiceProvider {
    /**
     * Holds all the requests which failed due to 401 response,
     * so they can be re-requested in future, once login is completed.
     */
    private buffer = [];

    /**
     * Required by HTTP interceptor.
     * Function is attached to provider to be invisible for regular users of this service.
     */
    private pushToBuffer = (config, deferred) => {
      this.buffer.push({
        config: config,
        deferred: deferred
      });
    };

    public $get = ['$rootScope', '$injector', ($rootScope, $injector) => {
      let $http; //initialized later because of circular dependency problem
      let tokenService: any;
      return {
        append: (config, deferred) => {
          this.buffer.push({
            config: config,
            deferred: deferred
          });
        },
        loginConfirmed: () => {

          $rootScope.$broadcast('event:auth-loginConfirmed');
          for (let i = 0; i < this.buffer.length; ++i) {
            $http = $http || $injector.get('$http');
            tokenService = tokenService || $injector.get('TokenService');
            this.buffer[i].config.params = this.buffer[i].config.params || {};
            this.buffer[i].config.params['access_token'] = tokenService.GetAccessToken();
            let deferred = this.buffer[i].deferred;
            $http(this.buffer[i].config).then((response) => {
              deferred.resolve(response);
            });
          }
          this.buffer = [];
        }
      }
    }]
  }
}

// Configure AuthService provider now that app namespace is defined
(window as any).frontendApp.provider('authService', new app.AuthService());

(window as any).frontendApp

  .config(['$routeProvider', '$locationProvider', '$httpProvider', '$uibTooltipProvider', '$animateProvider', ($routeProvider, $locationProvider, $httpProvider, $uibTooltipProvider, $animateProvider) => {

    $animateProvider.classNameFilter(/angular-animate/);

    $routeProvider
      .when('/', {templateUrl: 'views/home.html', controller: 'HomeController'})
      .when('/verify/:email/:accountId/:passwordResetToken', {
        templateUrl: 'views/reset.html',
        controller: 'ResetPasswordController',
        action: 'verify'
      })
      .when('/reset/:email/:accountId/:passwordResetToken', {
        templateUrl: 'views/reset.html',
        controller: 'ResetPasswordController',
        action: 'reset'
      })
      .when('/settings', {templateUrl: 'views/settings.html', controller: 'SettingsCtrl'})
      .when('/project', {templateUrl: 'views/project.html', controller: 'ProjectController'})
      .when('/project/:projectId', {
        templateUrl: 'views/project_detail.html',
        controller: 'ProjectDetailController'
      })
      .when('/project/:projectId/collections', {
        templateUrl: 'views/project_detail.html',
        controller: 'ProjectDetailController',
        tab: 'collection'
      })
      .when('/project/:projectId/getstarted', {
        templateUrl: 'views/get_started.html',
        controller: 'GetStartedController'
      })
      .when('/project/:projectId/admin', {
        templateUrl: 'views/project_admin.html',
        controller: 'ProjectAdminController'
      })
      .when('/project/:projectId/collection/:collectionId', {
        templateUrl: 'views/collection.html',
        controller: 'CollectionController',
        reloadOnSearch: false
      })
      .when('/project/:projectId/search', {
        templateUrl: 'views/search_results.html',
        controller: 'SearchController'
      })
      .when('/project/:projectId/browse', {
        templateUrl: 'views/project_browse.html',
        controller: 'BrowseController',
        reloadOnSearch: false
      })
      .when('/project/:projectId/collection/:collectionId/search', {
        templateUrl: 'views/search_results.html',
        controller: 'SearchController'
      })
      .when('/project/:projectId/home/batch/:batchId', {
        templateUrl: 'views/home_results.html',
        controller: 'BrowseController',
        action: "simplified_review",
        reloadOnSearch: false
      })
      .when('/project/:projectId/home/batch/:batchId/:reviewState', {
        templateUrl: 'views/home_results.html',
        controller: 'BrowseController',
        action: "simplified_review",
        reloadOnSearch: false
      })
      .when('/project/:projectId/home/search/:searchId', {
        templateUrl: 'views/home_results.html',
        controller: 'SearchController',
        action: "simplified_review",
        reloadOnSearch: false
      })
      .when('/project/:projectId/loadfile', {templateUrl: 'views/load_file.html', controller: 'LoadFileCtrl'})
      .when('/organization', {templateUrl: 'views/organization.html', controller: 'OrganizationCtrl'})
      .when('/notification', {templateUrl: 'views/notification.html', controller: 'NotificationCtrl'})
      .when('/pagenotfound', {templateUrl: 'views/404.html', controller: '404Ctrl'})
      .otherwise({redirectTo: '/pagenotfound'});

    $locationProvider.hashPrefix('!');

    $uibTooltipProvider.setTriggers({'iioPopoverOpen': 'iioPopoverClose'});

    $httpProvider.interceptors.push('myHttpInterceptor');

    // CORS stuff
    $httpProvider.defaults.useXDomain = true;
    delete $httpProvider.defaults.headers.common['X-Requested-With'];
    $httpProvider.defaults.headers.delete = {'Content-Type': 'application/json;charset=utf-8'};
    $httpProvider.defaults.headers.get = {'Content-Type': 'application/json;charset=utf-8'};

    //$httpProvider.defaults.headers.delete = {'Content-Type': 'application/json;charset=utf-8'};
    $httpProvider.defaults.timeout = 120000;

  }])

  .run(['$rootScope', '$http', '$location', '$window', '$log', 'TokenService', 'AccountService', 'ConfigService', 'StateService', 'WebSocketService', 'ThemeService', 'TrackService', 'AuthorizationRefreshService', 'HttpService', ($rootScope, $http, $location, $window, $log, tokenService: app.services.authentication.TokenService, accountService: app.services.account.AccountService, configService: app.services.IConfigService, stateService: app.services.state.StateService, webSocketService, themeService, trackService: app.services.auditing.TrackService, authorizationRefreshService, httpService: app.services.IHttpWrapperService) => {

    configService
      .setAppVersion(__APP_VERSION__)
      .setServerBaseUrl(import.meta.env.VITE_API_URL || 'http://localhost:8080/iio')
      .setFrontendUrl($window.location.protocol + '//' + $window.location.hostname + ($window.location.port ? ':' + $window.location.port : ''))
      .setEnvironment(import.meta.env.VITE_ENVIRONMENT || 'dev');

    if (import.meta.env.VITE_STRIPE_KEY) {
      configService.setStripePubKey(import.meta.env.VITE_STRIPE_KEY);
    }
    //configService.setMaintenanceMode(true);

    const setZopimAccount = () => {
      console.log("Setting name and email for zopim");
      $window.$zopim(function () {
        $window.$zopim.livechat.setName(accountService.GetFullName());
        $window.$zopim.livechat.setEmail(accountService.GetEmail());
      });
    };

    $rootScope.$on('AccountService.accountUpdated', () => setZopimAccount());

    stateService.SetLoading();

    console.log('CONFIG: App Version: ' + configService.getAppVersion());
    console.log('CONFIG: Server Base Url: ' + configService.getServerBaseUrl());
    console.log('CONFIG: Server Base Url For Resource: ' + configService.getServerBaseUrlForResource());
    console.log('CONFIG: Frontend Url: ' + configService.getFrontendUrl());

    themeService.findThemeForSubdomain();

    $rootScope.features = (window as any).__features || {};
    window.addEventListener('featuresLoaded', function(e: any) {
      $rootScope.$apply(function() { $rootScope.features = e.detail; });
    }, { once: true });

    $rootScope.loginMessage = null;
    $http.get("https://iio-assets.s3.amazonaws.com/messages/messages.json")
      .then(function (response) {
        let data = response.data as any;
        let today = new Date();
        data.messages.forEach((message) => {
          let messageStart = new Date(message['start'])
          let messageEnd = new Date(message['end'])
          if (messageStart < today && today < messageEnd) {
            $rootScope.loginMessage = {
              "content": message['content'],
              "title": message['title']
            };
          }
        });
      });

    if (configService.isMaintenanceMode()) {
      console.log("In maintenance mode");

      tokenService.Clear();
      stateService.SetLoggedOut();
      $location.path('/');

      // Now see where we are with our token and account id
    } else if (tokenService.Exists()) {

      // load our account
      console.log('Requesting details for account');
      accountService.Fetch();

      console.log('Account: ', accountService.Get());
      console.log('Token: ', tokenService.Get());

      setZopimAccount();

      // After successfully get the account info
      stateService.SetLoggedIn();

      // Logged-in users landing on the home/login page should go to /project
      if ($location.path() === '/') {
        $location.path('/project');
      }

    } else {
      tokenService.Clear();
      stateService.SetLoggedOut();

      if ($location.path() && !$location.path().startsWith('/reset/') && !$location.path().startsWith('/verify/')) {
        $location.path('/');
      }
    }

    // Route-level auth guards — enforced on every navigation, not just initial load.
    const publicPaths = ['/', '/pagenotfound'];
    const isPublicPath = (path: string) =>
      publicPaths.includes(path) ||
      path.startsWith('/reset/') ||
      path.startsWith('/verify/');

    $rootScope.$on('$routeChangeStart', (_event, next) => {
      const path = next && next.$$route && next.$$route.originalPath || $location.path();
      if (stateService.IsLoggedIn() && path === '/') {
        $location.path('/project');
      } else if (stateService.IsLoggedOut() && !stateService.IsLoading() && !isPublicPath(path)) {
        $location.path('/');
      }
    });

    $rootScope.$on('$routeChangeSuccess', () => {
      trackService.TrackPageView($location.path());
    });
  }]);
